import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Stack, PasswordInput, Button } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { PageIntro } from '@/components/rides/PageIntro';
import { PasswordRules, passwordMeetsRules } from '@/components/auth/PasswordRules';
import { Callout, LoadingState } from '@/components/ui';
import { useCopy } from '@/lib/settings';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authService = useAuthService();
  const { t } = useCopy();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ password?: string; confirm?: string }>({});
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ok = await authService.validateResetToken(token, email);
        if (!cancelled) setTokenValid(ok);
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, email, authService]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const fe: { password?: string; confirm?: string } = {};
    if (!passwordMeetsRules(password)) fe.password = 'Your password needs everything in the list below.';
    if (password !== confirmPassword) fe.confirm = "The two passwords don't match.";
    setFieldError(fe);
    if (fe.password || fe.confirm) return;
    setSubmitting(true);
    try {
      await authService.resetPassword({ email, password, confirmPassword, code: token });
      setDone(true);
      setTimeout(() => navigate('/auth/login', { replace: true }), 2500);
    } catch {
      setError("We couldn't change your password. The link may have expired. Please ask for a new one.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rides-main">
      <PageIntro title="Choose a new password" lede={tokenValid && !done && email ? t('reset.intro', undefined, { email }) : undefined} />
      <div className="rides-card" style={{ padding: 18 }}>
        {validating ? (
          <LoadingState variant="spinner" label="Checking your link" />
        ) : !tokenValid ? (
          <Stack gap="md">
            <Callout tone="bad" live>{t('reset.invalid')}</Callout>
            <Button component={Link} to="/auth/forgot-password" fullWidth>Get a new link</Button>
          </Stack>
        ) : done ? (
          <Callout tone="ok" live>{t('reset.done')}</Callout>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <Stack gap="md">
              {error && <Callout tone="bad" live>{error}</Callout>}
              <div>
                <PasswordInput
                  label="New password"
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.currentTarget.value); setFieldError((f) => ({ ...f, password: undefined })); }}
                  autoComplete="new-password"
                  error={fieldError.password}
                  aria-describedby="reset-password-rules"
                  mb={8}
                />
                <PasswordRules password={password} id="reset-password-rules" />
              </div>
              <PasswordInput
                label="Type it again"
                required
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.currentTarget.value); setFieldError((f) => ({ ...f, confirm: undefined })); }}
                autoComplete="new-password"
                error={fieldError.confirm}
              />
              <Button type="submit" loading={submitting} leftSection={<IconLock size={20} />} fullWidth>Save new password</Button>
            </Stack>
          </form>
        )}
      </div>
    </div>
  );
}
